import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {finalize, map, share} from 'rxjs/operators';

/**
 * Identical GETs that are already on the wire share one response instead of going out twice.
 *
 * Opening an order fires the same lookups several times within a few milliseconds: the saved order is patched into the
 * form and every field's change handler re-requests what onEntityReceipt has just asked for (customer, business type,
 * service levels, goods types, extras ...), and the toolbar and app shell load their settings once per identity event.
 * Each caller still runs exactly as before - it only waits on the request that is already in flight rather than
 * starting its own.
 *
 * /api/account is deliberately NOT shared: the route guard waiting on its own account request is what gives the tenant
 * currency time to load before a screen renders, and several pipes read that currency only once.
 *
 * Deliberately narrow:
 *  - only while the first request is pending; once it settles the next call goes to the server as normal, so nothing
 *    is cached and nothing can go stale;
 *  - only JSON GETs whose URL matches {@link SHAREABLE}, read-only lookups that were seen being requested in parallel;
 *  - same URL, same query string, same Authorization header;
 *  - every caller gets its own deep copy of the body, so one caller mutating its result cannot leak into another.
 *
 * Registered after the other interceptors, so auth, error handling and notifications still run once per caller.
 */
const SHAREABLE = new RegExp(
    '/api/(' +
        [
            'tenant-settings/all',
            'currencies/tenant',
            'exchange-rates/conversion-chart',
            'roles/settings',
            'translation/all',
            'notifications',
            'async-requests/in-progress',
            'vehicle-classes-lite',
            'extra-types/enabled',
            'business-types/\\d+',
            'location-points/\\d+',
            'organisations/\\d+',
            'organisations/\\d+/(service-levels|good-types|business-types|location-points|order-docs|defaults)',
            'organisation-extra-types/\\d+/organisation-extras',
            'organisation-custom-references/\\d+',
            'customer-organisation-setting/\\d+/[A-Za-z]+'
        ].join('|') +
        ')$'
);

@Injectable()
export class InFlightGetInterceptor implements HttpInterceptor {
    private readonly _pending = new Map<string, Observable<HttpEvent<any>>>();

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (request.method !== 'GET' || request.responseType !== 'json' || !SHAREABLE.test(request.url.split('?')[0])) {
            return next.handle(request);
        }
        const key = request.urlWithParams + '|' + (request.headers.get('Authorization') || '');
        let shared = this._pending.get(key);
        if (!shared) {
            shared = next.handle(request).pipe(
                finalize(() => this._pending.delete(key)),
                share()
            );
            this._pending.set(key, shared);
        }
        return shared.pipe(map((event) => (event instanceof HttpResponse ? event.clone({body: structuredClone(event.body)}) : event)));
    }
}
